import React, { useEffect, useState, Children } from 'react';
import { PlusIcon, SearchIcon, FilterIcon, EyeIcon, CheckCircleIcon, AlertTriangleIcon, XCircleIcon, BuildingIcon, MapPinIcon, GlobeIcon, ChevronLeftIcon, ChevronRightIcon, BarChartIcon, Edit2Icon, Trash2Icon, NetworkIcon } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
import { VendorCard } from './VendorCard';
export function VendorList({
  vendors,
  onViewVendor,
  setCurrentView,
  locale
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [complianceFilter, setComplianceFilter] = useState('all');
  const [esgFilter, setESGFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isESGEnabled] = useState(true);
  const [hasESGViewPermission] = useState(true);
  const [showESGColumn, setShowESGColumn] = useState(true);
  // Check for status filter from dashboard on mount
  useEffect(() => {
    const savedFilter = sessionStorage.getItem('vendorStatusFilter');
    if (savedFilter) {
      setStatusFilter(savedFilter);
      // Clear the filter after applying it
      sessionStorage.removeItem('vendorStatusFilter');
    }
  }, []);
  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = (vendor.vendorName || '').toLowerCase().includes(searchTerm.toLowerCase()) || (vendor.vendorId || '').toLowerCase().includes(searchTerm.toLowerCase()) || (vendor.vendorCode || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || vendor.vendorStatus?.name === statusFilter;
    let matchesCompliance = true;
    if (complianceFilter !== 'all') {
      if (complianceFilter === 'Compliant') {
        matchesCompliance = vendor.isCompliant === true;
      } else if (complianceFilter === 'Non-Compliant') {
        matchesCompliance = vendor.isCompliant === false;
      }
    }
    let matchesESG = true;
    if (isESGEnabled && esgFilter !== 'all') {
      if (esgFilter === 'not-provided') {
        matchesESG = !vendor.esg || !vendor.esg.score;
      } else if (vendor.esg && vendor.esg.score !== undefined) {
        if (esgFilter === 'excellent') matchesESG = vendor.esg.score >= 80;else if (esgFilter === 'good') matchesESG = vendor.esg.score >= 60 && vendor.esg.score < 80;else if (esgFilter === 'moderate') matchesESG = vendor.esg.score >= 40 && vendor.esg.score < 60;else if (esgFilter === 'needs-review') matchesESG = vendor.esg.score < 40;
      } else {
        matchesESG = false;
      }
    }
    return matchesSearch && matchesStatus && matchesCompliance && matchesESG;
  });
  const totalVendors = filteredVendors.length;
  const totalPages = Math.ceil(totalVendors / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalVendors);
  const currentVendors = filteredVendors.slice(startIndex, endIndex);
  const getAvailabilityText = vendor => {
    if (!vendor.orgUnitId) {
      return 'All Entities';
    }
    if (vendor.includeChildOrgUnits) {
      return `${vendor.orgUnit?.name || 'OrgUnit'} + Children`;
    }
    return vendor.orgUnit?.name || 'Specific OrgUnit';
  };
  const getAvailabilityIcon = vendor => {
    if (!vendor.orgUnitId) {
      return <GlobeIcon className="h-4 w-4 text-blue-500" data-id="element-22139" />;
    }
    if (vendor.includeChildOrgUnits) {
      return <NetworkIcon className="h-4 w-4 text-purple-500" data-id="element-22140" />;
    }
    return <BuildingIcon className="h-4 w-4 text-indigo-500" data-id="element-22141" />;
  };
  const getAvailabilityTooltip = vendor => {
    if (!vendor.orgUnitId) {
      return 'Available to all entities globally';
    }
    if (vendor.includeChildOrgUnits) {
      return `Available to ${vendor.orgUnit?.name} and all child org units`;
    }
    return `Available only to ${vendor.orgUnit?.name}`;
  };
  const getComplianceBadge = vendor => {
    if (vendor.isCompliant) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-22142">
          <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-22143" />
          Compliant
        </span>;
    } else {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-22144">
          <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-22145" />
          Non-Compliant
        </span>;
    }
  };
  const getESGStatusBadge = vendor => {
    if (!vendor.esg || vendor.esg.score === undefined) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-22146">
          Not Provided
        </span>;
    }
    const score = vendor.esg.score;
    if (score >= 80) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-22147">
          <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-22148" />
          Excellent
        </span>;
    } else if (score >= 60) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-22149">
          <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-22150" />
          Good
        </span>;
    } else if (score >= 40) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-22151">
          <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-22152" />
          Moderate
        </span>;
    } else {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-22153">
          <XCircleIcon className="mr-1 h-3 w-3" data-id="element-22154" />
          Needs Review
        </span>;
    }
  };
  const goToPage = page => {
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };
  const handlePageSizeChange = e => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
  };
  // Show active filter indicator
  const hasActiveFilters = statusFilter !== 'all' || complianceFilter !== 'all' || esgFilter !== 'all' || searchTerm !== '';
  return <div data-id="element-22155">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-22156">
        <div className="flex-1 max-w-md" data-id="element-22157">
          <div className="relative rounded-md shadow-sm" data-id="element-22158">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-22159">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-22160" />
            </div>
            <input type="text" value={searchTerm} onChange={e => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search vendors..." data-id="element-22161" />
          </div>
        </div>
        <div className="flex space-x-2" data-id="element-22162">
          <div data-id="element-22163">
            <select value={statusFilter} onChange={e => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-22164">
              <option value="all" data-id="element-22165">All Statuses</option>
              <option value="Active" data-id="element-22166">Active</option>
              <option value="Pending Onboarding" data-id="element-22167">Pending Onboarding</option>
              <option value="Inactive" data-id="element-22168">Inactive</option>
              <option value="Invited" data-id="element-22169">Invited</option>
              <option value="Suspended" data-id="element-22170">Suspended</option>
            </select>
          </div>
          <div data-id="element-22171">
            <select value={complianceFilter} onChange={e => {
            setComplianceFilter(e.target.value);
            setCurrentPage(1);
          }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-22172">
              <option value="all" data-id="element-22173">All Compliance</option>
              <option value="Compliant" data-id="element-22174">Compliant</option>
              <option value="Non-Compliant" data-id="element-22175">Non-Compliant</option>
            </select>
          </div>
          {isESGEnabled && hasESGViewPermission && <div data-id="element-22176">
              <select value={esgFilter} onChange={e => {
            setESGFilter(e.target.value);
            setCurrentPage(1);
          }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-22177">
                <option value="all" data-id="element-22178">All ESG Status</option>
                <option value="excellent" data-id="element-22179">Excellent (80+)</option>
                <option value="good" data-id="element-22180">Good (60-79)</option>
                <option value="moderate" data-id="element-22181">Moderate (40-59)</option>
                <option value="needs-review" data-id="element-22182">Needs Review (&lt;40)</option>
                <option value="not-provided" data-id="element-22183">Not Provided</option>
              </select>
            </div>}
          <div className="inline-flex shadow-sm rounded-md" data-id="element-22184">
            <button onClick={() => setViewMode('table')} className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-l-md ${viewMode === 'table' ? 'bg-indigo-100 text-indigo-800' : 'bg-white text-gray-700 hover:bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-22185">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-22186">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" data-id="element-22187" />
              </svg>
            </button>
            <button onClick={() => setViewMode('grid')} className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-r-md ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-800' : 'bg-white text-gray-700 hover:bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-22188">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-22189">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" data-id="element-22190" />
              </svg>
            </button>
          </div>
          <button onClick={() => setCurrentView('onboard-vendor')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22191">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-22192" />
            Add Vendor
          </button>
        </div>
      </div>

      {/* Active filter indicator */}
      {hasActiveFilters && <div className="mb-4 flex items-center gap-2" data-id="element-22193">
          <span className="text-sm text-gray-600" data-id="element-22194">Active filters:</span>
          {statusFilter !== 'all' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-22195">
              Status: {statusFilter}
              <button onClick={() => setStatusFilter('all')} className="ml-1 inline-flex items-center" data-id="element-22196">
                <XCircleIcon className="h-3 w-3" data-id="element-22197" />
              </button>
            </span>}
          {complianceFilter !== 'all' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-22198">
              Compliance: {complianceFilter}
              <button onClick={() => setComplianceFilter('all')} className="ml-1 inline-flex items-center" data-id="element-22199">
                <XCircleIcon className="h-3 w-3" data-id="element-22200" />
              </button>
            </span>}
          {esgFilter !== 'all' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-22201">
              ESG: {esgFilter}
              <button onClick={() => setESGFilter('all')} className="ml-1 inline-flex items-center" data-id="element-22202">
                <XCircleIcon className="h-3 w-3" data-id="element-22203" />
              </button>
            </span>}
          {searchTerm && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-22204">
              Search: "{searchTerm}"
              <button onClick={() => setSearchTerm('')} className="ml-1 inline-flex items-center" data-id="element-22205">
                <XCircleIcon className="h-3 w-3" data-id="element-22206" />
              </button>
            </span>}
          <button onClick={() => {
        setStatusFilter('all');
        setComplianceFilter('all');
        setESGFilter('all');
        setSearchTerm('');
      }} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-22207">
            Clear all
          </button>
        </div>}

      {isESGEnabled && hasESGViewPermission && <div className="mb-4 flex items-center" data-id="element-22208">
          <label className="flex items-center text-sm text-gray-700" data-id="element-22209">
            <input type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={showESGColumn} onChange={e => setShowESGColumn(e.target.checked)} data-id="element-22210" />
            <span className="ml-2" data-id="element-22211">Show ESG Score column</span>
          </label>
        </div>}

      <div className="flex justify-between items-center mb-4" data-id="element-22212">
        <div className="text-sm text-gray-700" data-id="element-22213">
          Showing <span className="font-medium" data-id="element-22214">{startIndex + 1}</span> to{' '}
          <span className="font-medium" data-id="element-22215">{endIndex}</span> of{' '}
          <span className="font-medium" data-id="element-22216">{totalVendors}</span> vendors
        </div>
        <div className="flex items-center space-x-2" data-id="element-22217">
          <span className="text-sm text-gray-700" data-id="element-22218">Rows per page:</span>
          <select value={pageSize} onChange={handlePageSizeChange} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-22219">
            <option value={10} data-id="element-22220">10</option>
            <option value={25} data-id="element-22221">25</option>
            <option value={50} data-id="element-22222">50</option>
            <option value={100} data-id="element-22223">100</option>
          </select>
        </div>
      </div>

      {viewMode === 'table' && <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-22224">
          {currentVendors.length === 0 ? <div className="px-4 py-8 text-center text-gray-500" data-id="element-22225">
              No vendors found matching your search criteria.
            </div> : <div className="overflow-x-auto" data-id="element-22226">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-22227">
                <thead className="bg-gray-50" data-id="element-22228">
                  <tr data-id="element-22229">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22230">
                      Vendor
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22231">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22232">
                      Availability
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22233">
                      Accounts
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22234">
                      Country
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22235">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22236">
                      Compliance
                    </th>
                    {isESGEnabled && hasESGViewPermission && showESGColumn && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22237">
                        ESG Score
                      </th>}
                    <th scope="col" className="sticky right-0 bg-gray-50 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider shadow-sm" data-id="element-22238">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-22239">
                  {currentVendors.map(vendor => <tr key={vendor.vendorId} className="hover:bg-gray-50" data-id="element-22240">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-22241">
                        <div className="text-sm font-medium text-indigo-600" data-id="element-22242">
                          {vendor.vendorName}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-22243">
                          {vendor.vendorCode || vendor.vendorId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-22244">
                        {vendor.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-22245">
                        <div className="flex items-center text-sm text-gray-500" title={getAvailabilityTooltip(vendor)} data-id="element-22246">
                          {getAvailabilityIcon(vendor)}
                          <span className="ml-1" data-id="element-22247">
                            {getAvailabilityText(vendor)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-22248">
                        {(vendor.accounts?.length || 0) > 0 ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-22249">
                            {vendor.accounts.length}
                          </span> : <span className="text-sm text-gray-400" data-id="element-22250">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-22251">
                        {vendor.country}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-22252">
                        <StatusBadge status={vendor.vendorStatus?.code?.toLowerCase() || 'active'} text={vendor.vendorStatus?.name || 'Active'} data-id="element-22253" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-22254">
                        {getComplianceBadge(vendor)}
                      </td>
                      {isESGEnabled && hasESGViewPermission && showESGColumn && <td className="px-6 py-4 whitespace-nowrap" data-id="element-22255">
                            {vendor.esg ? <div className="flex items-center" data-id="element-22256">
                                <span className="text-sm font-medium mr-2" data-id="element-22257">
                                  {vendor.esg.score !== undefined ? vendor.esg.score : 'N/A'}
                                </span>
                                {vendor.esg.score !== undefined && getESGStatusBadge(vendor)}
                              </div> : <span className="text-sm text-gray-500" data-id="element-22258">N/A</span>}
                          </td>}
                      <td className="sticky right-0 bg-white px-6 py-4 whitespace-nowrap text-center shadow-sm" data-id="element-22259">
                        <div className="flex items-center justify-center space-x-2" data-id="element-22260">
                          <button onClick={() => {
                    onViewVendor(vendor);
                    // Set editing mode flag
                    sessionStorage.setItem('vendorEditMode', 'true');
                  }} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-22261">
                            <Edit2Icon className="h-4 w-4" data-id="element-22262" />
                          </button>
                          <button onClick={() => onViewVendor(vendor)} className="text-green-600 hover:text-green-900" title="View Details" data-id="element-22263">
                            <EyeIcon className="h-4 w-4" data-id="element-22264" />
                          </button>
                          <button onClick={() => {
                    if (window.confirm(`Are you sure you want to delete ${vendor.vendorName}?`)) {
                      console.log('Delete vendor:', vendor.vendorId);
                      // In a real app, this would call a delete handler
                    }
                  }} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-22265">
                            <Trash2Icon className="h-4 w-4" data-id="element-22266" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>}

      {viewMode === 'grid' && <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-22267">
          {currentVendors.length === 0 ? <div className="col-span-full text-center py-12 bg-white rounded-lg shadow" data-id="element-22268">
              <div className="mx-auto h-12 w-12 text-gray-400 flex items-center justify-center" data-id="element-22269">
                <BuildingIcon className="h-12 w-12" data-id="element-22270" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-22271">
                No vendors found
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-22272">
                Try adjusting your search or filters.
              </p>
              <div className="mt-6" data-id="element-22273">
                <button onClick={() => setCurrentView('onboard-vendor')} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22274">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-22275" />
                  Add Vendor
                </button>
              </div>
            </div> : currentVendors.map(vendor => <VendorCard key={vendor.vendorId} vendor={vendor} onViewVendor={onViewVendor} getAvailabilityIcon={getAvailabilityIcon} getAvailabilityText={getAvailabilityText} locale={locale} isESGEnabled={isESGEnabled && hasESGViewPermission && showESGColumn} data-id="element-22276" />)}
        </div>}

      {totalVendors > 0 && <div className="mt-4 flex items-center justify-between" data-id="element-22277">
          <div className="flex-1 flex justify-between sm:hidden" data-id="element-22278">
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-22279">
              Previous
            </button>
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-22280">
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-22281">
            <div data-id="element-22282">
              <p className="text-sm text-gray-700" data-id="element-22283">
                Page <span className="font-medium" data-id="element-22284">{currentPage}</span> of{' '}
                <span className="font-medium" data-id="element-22285">{totalPages}</span>
              </p>
            </div>
            <div data-id="element-22286">
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-22287">
                <button onClick={() => goToPage(1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-22288">
                  <span className="sr-only" data-id="element-22289">First Page</span>
                  <div className="h-5 w-5" aria-hidden="true" data-id="element-22290" />
                </button>
                <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-22291">
                  <span className="sr-only" data-id="element-22292">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-22293" />
                </button>
                {Array.from({
              length: Math.min(5, totalPages)
            }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return <button key={pageNum} onClick={() => goToPage(pageNum)} className={`relative inline-flex items-center px-4 py-2 border ${currentPage === pageNum ? 'bg-indigo-50 border-indigo-500 text-indigo-600 z-10' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'} text-sm font-medium`} data-id="element-22294">
                        {pageNum}
                      </button>;
            })}
                <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-22295">
                  <span className="sr-only" data-id="element-22296">Next</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-22297" />
                </button>
                <button onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-22298">
                  <span className="sr-only" data-id="element-22299">Last Page</span>
                  <div className="h-5 w-5" aria-hidden="true" data-id="element-22300" />
                </button>
              </nav>
            </div>
          </div>
        </div>}
    </div>;
}